import React, { useEffect } from "react";
import { Divider, Stack, Typography } from "@mui/material";
import { FOUNDRIES } from "../../constants/industry";
import {
  HubHookFormSelect,
  HubHookFormInput,
  HubButton,
  HubHookFormSwitch,
  HubHookCustomersSelect,
} from "../../components";
import { SHIPPING } from "../../constants";
import { ORDER_TYPE_OPTIONS, ORDER_TYPES } from "../../constants/orders";
import { useFormContext } from "react-hook-form";
import { useGetCustomerDetailsById } from "../../hooks";

const getAddressLabel = (add) => {
  const addressString = `${add?.street}, ${add?.city} ${add?.state}, ${add?.country} ${add?.zip}`;
  const type =
    add.billing && add.shipping
      ? "Main"
      : add.billing
        ? "Billing"
        : add.shipping
          ? "Shipping"
          : "General";
  return (
    <Stack>
      <Stack direction="row" gap="5px" alignItems="center">
        <Typography variant="boldCaption">{type}</Typography>
        {add?.deleted && (
          <Typography variant="boldCaptionMain">Archived</Typography>
        )}
      </Stack>
      <Typography variant="caption">{addressString}</Typography>
    </Stack>
  );
};

const PoDetails = ({ customerId, isLoading = false }) => {
  const { watch, setValue } = useFormContext();

  const cid = watch("customerId");

  const {
    data,
    isLoading: isCustomerLoading,
    isSuccess,
  } = useGetCustomerDetailsById(cid);

  const disabled = isLoading || isCustomerLoading;
  const isVendor = data?.isVendor;

  useEffect(() => {
    if (data && isSuccess) setValue("isVendor", data?.isVendor);
  }, [data, isSuccess]);

  const addressOptions = data?.addresses
    ?.filter((a) => a?.id)
    ?.map((add) => ({
      id: add.id,
      label: getAddressLabel(add),
    }));

  return (
    <Stack gap="15px">
      <Typography sx={{ fontWeight: 600 }}>Order Details</Typography>
      <HubHookCustomersSelect
        name="customerId"
        defaultValue={customerId}
        rendered={false}
        required
      />
      <Stack
        gap="10px"
        sx={{ flexDirection: { sm: "column", md: "row" } }}
        alignItems="center"
      >
        {isVendor ? (
          <HubHookFormSelect
            name="type"
            label="Order Type"
            options={ORDER_TYPE_OPTIONS.filter((f) => f.isOutbound)}
            defaultValue={ORDER_TYPES.MATERIALS}
            required
          />
        ) : (
          <HubHookFormInput
            name="poNumber"
            label="Customer PO#"
            placeholder="Order #"
            disableHelperText
            required
          />
        )}
        <HubHookFormSelect
          name="location"
          label="Foundry"
          options={FOUNDRIES}
          disableHelperText
          required
        />
        <HubHookFormSwitch
          name="overShipping"
          label="Over Shipping"
          disableHelperText
          isCheckbox
        />
      </Stack>
      <Divider
        orientation="horizontal"
        flexItem
        sx={{ border: "1px dashed", borderColor: "hubColors.greyLight" }}
      />
      <Stack gap="15px">
        <Typography variant="body2">Shipping</Typography>
        <Stack gap="10px" sx={{ flexDirection: { sm: "column", md: "row" } }}>
          <HubHookFormInput
            name="freight"
            label="Freight"
            placeholder="X"
            disableHelperText
            defaultValue={data?.defaultFreight}
            required
          />
          <HubHookFormSelect
            name="shipVia"
            label="Ship Via"
            options={SHIPPING}
            defaultValue={data?.defaultShipVia}
            disableHelperText
            required
          />
        </Stack>
        <HubHookFormSelect
          name="shipTo"
          label={isVendor ? "Bill To" : "Ship To"}
          options={addressOptions}
          defaultValue={data?.defaultShipTo}
          disableHelperText
          required
        />
      </Stack>
      <HubButton
        variant="contained"
        size="small"
        label="Create Purchase Order"
        disabled={disabled}
        type="submit"
      />
    </Stack>
  );
};

export default PoDetails;
